import { Api } from "@/api/Api";
import { updateProfile } from "@/api/hooks/profile";
import { createTab, fetchTab, removeTab, updateTabOrders } from "@/api/hooks/tab";
import { importTab } from "@/api/hooks/tab";
import { useUser } from "@/api/hooks/user";
import type { TabData } from "@/components/organisms/TabDropdown/TabDropdown";
import { newEmptyTab } from "@/constants/create";
import {
  BufferName,
  useLocalClipboard,
} from "@/overlay/components/LayerStackWindow/ManageActionPanel/LocalClipboardContext";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { useAppDispatch } from "@/reducer";
import { setProfileTabs } from "@/reducer/ProfileState";
import { setTabOrder, setTabTitle } from "@/reducer/TabsState";
import { navigateToTab } from "@/route";
import { getFeatureValue } from "@/types/featureSet";
import { resolveActiveTab, setLastActiveTab } from "@/utils/lastActiveTab";
import { isProfileLocked } from "@/utils/profileLock";
import { t } from "@lingui/core/macro";
import type { SlimProfile, SlimProfileAccess } from "@mm/metx-workbench.meteomatics.com";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

function findNearestTab(tabs: SlimProfile["tabs"], deletedTabId: number) {
  const currentIndex = tabs.findIndex((tab) => tab.id === deletedTabId);
  if (currentIndex === -1) {
    return null;
  }

  // Try next tab first, then previous
  if (currentIndex < tabs.length - 1) {
    return tabs[currentIndex + 1];
  }
  if (currentIndex > 0) {
    return tabs[currentIndex - 1];
  }

  return null;
}
export function useTabManagement() {
  const desktop = useDesktop();
  const match = useRouteMatch();
  const {
    readData: readFromClipboard,
    storeData: storeToClipboard,
    clearData: clearClipboardData,
  } = useLocalClipboard<string>();
  const dispatch = useAppDispatch();
  const { features, user } = useUser();
  const userId = user?.id;
  const canAdd = !!getFeatureValue(features, "ui.tab.add");
  const canEdit = !!getFeatureValue(features, "ui.tab.edit");
  const canDelete = !!getFeatureValue(features, "ui.tab.delete");

  const getTabData = useCallback((profile: SlimProfileAccess): TabData[] => {
    if (!profile?.tabs) {
      return [];
    }

    return [...profile.tabs]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((tab) => ({
        id: tab.id.toString(),
        title: tab.title ?? "Untitled",
      }));
  }, []);

  const getCurrentTabId = useCallback((profile: SlimProfile) => resolveActiveTab(profile, userId), [userId]);

  // Locked profiles cannot persist `tab_active`, so the viewer's choice is kept in local storage instead.
  const persistActiveTab = useCallback(
    (profile: SlimProfileAccess, tabId: number) => {
      if (isProfileLocked(profile)) {
        setLastActiveTab(userId, profile.id, tabId);
        return;
      }
      dispatch(
        updateProfile({
          profileId: profile.id,
          data: { id: profile.id, tab_active: tabId },
          isLocked: profile.locked,
        }),
      );
    },
    [dispatch, userId],
  );

  const handleTabSelect = useCallback(
    (profile: SlimProfileAccess, tabId: number) => {
      desktop.dispatch({
        type: "setWindowVisibility",
        windowId: instanceId(WindowKind.PopupPlot),
        isVisible: false,
      });

      dispatch(fetchTab({ tabId }));
      persistActiveTab(profile, tabId);
      navigateToTab(match.path, profile.id, tabId);
    },
    [desktop, dispatch, match.path, persistActiveTab],
  );

  const handleTabPaste = useCallback(
    (profileId: number) => {
      if (!canAdd) {
        return;
      }
      const tabIdString = readFromClipboard(BufferName.Tab);

      if (!tabIdString) {
        return;
      }

      clearClipboardData(BufferName.Tab);

      dispatch(importTab({ tabId: Number.parseInt(tabIdString, 10), profileId }))
        .unwrap()
        .catch((e) => console.error(e));
    },
    [readFromClipboard, clearClipboardData, dispatch, canAdd],
  );

  const handleTabCreate = useCallback(
    (profile: SlimProfile, tabTitle: string) => {
      if (!canAdd) {
        return;
      }

      desktop.dispatch({
        type: "setWindowVisibility",
        windowId: instanceId(WindowKind.PopupPlot),
        isVisible: false,
      });

      newEmptyTab.title = tabTitle;

      dispatch(createTab({ profileId: profile.id, data: newEmptyTab }))
        .unwrap()
        .then((tab) => handleTabSelect(profile, tab.id))
        .catch((e) => console.error(e));
    },
    [desktop, dispatch, handleTabSelect, canAdd],
  );

  const handleTabRename = useCallback(
    (profile: SlimProfileAccess, tabIdString: string, newTitle: string) => {
      if (!profile || !canEdit) {
        return;
      }

      const tabId = Number.parseInt(tabIdString, 10);

      dispatch(setTabTitle({ profileId: profile.id, tabId, title: newTitle }));
      Api.tab.v2RenameTab({ tabId, title: newTitle }).catch((e) => {
        console.error("Could not rename tab", e);
      });
    },
    [dispatch, canEdit],
  );

  const handleTabCopy = useCallback(
    (tabIdString: string) => {
      storeToClipboard(BufferName.Tab, tabIdString);
    },
    [storeToClipboard],
  );

  const handleTabDelete = useCallback(
    (profile: SlimProfileAccess, tabIdString: string) => {
      if (!profile || !canDelete) {
        return;
      }

      const tabIdNum = Number.parseInt(tabIdString, 10);
      const selectedTab = profile.tabs.find((tab) => tab.id === tabIdNum);
      if (!selectedTab) {
        return;
      }

      const currentTabId = getCurrentTabId(profile);

      desktop.dispatch({
        type: "confirmationWindow",
        windowId: instanceId(WindowKind.ConfirmationWindow),
        customProps: {
          message: t`Do you want to delete tab "${selectedTab.title ?? ""}"?`,
          callback: (confirmed: boolean) => {
            if (!confirmed) {
              return;
            }

            if (selectedTab.id === currentTabId) {
              const nearestTab = findNearestTab(profile.tabs, selectedTab.id);

              if (nearestTab) {
                dispatch(fetchTab({ tabId: nearestTab.id }));
                dispatch(removeTab({ tabId: selectedTab.id, profileId: profile.id }));
                persistActiveTab(profile, nearestTab.id);
                navigateToTab(match.path, profile.id, nearestTab.id);
              }
            } else {
              dispatch(removeTab({ tabId: selectedTab.id, profileId: profile.id }));
            }
          },
        },
      });
    },
    [desktop, dispatch, match.path, getCurrentTabId, persistActiveTab, canDelete],
  );

  const handleTabReorder = useCallback(
    (profile: SlimProfileAccess, newOrder: TabData[]) => {
      if (!profile || !canEdit) {
        return;
      }

      // Preserve the full tab objects so a reorder cannot drop metadata such as
      // title, timestamps, flags, or nested relationships from the local profile cache.
      const reorderedTabs = newOrder
        .map((tabData, index) => {
          const tabId = Number.parseInt(tabData.id, 10);
          const currentTab = profile.tabs.find((tab) => tab.id === tabId);

          if (Number.isNaN(tabId) || !currentTab) {
            return null;
          }

          return {
            ...currentTab,
            order: index + 1,
          };
        })
        .filter((tab): tab is NonNullable<typeof tab> => tab !== null);

      const currentTabId = getCurrentTabId(profile);
      if (currentTabId) {
        dispatch(
          setTabOrder({
            profileId: profile.id,
            id: currentTabId,
            order: reorderedTabs.find((tab) => tab.id === currentTabId)?.order ?? 0,
          }),
        );
      }

      // Persist the new order first, then update the cached profile tabs with the
      // full tab objects instead of a stripped `tabs` payload.
      dispatch(updateTabOrders({ tabs: reorderedTabs }));
      dispatch(setProfileTabs({ id: profile.id, tabs: reorderedTabs }));
    },
    [dispatch, getCurrentTabId, canEdit],
  );

  return {
    // Utility functions
    getTabData,
    getCurrentTabId,
    // Actions
    handleTabSelect,
    handleTabCreate,
    handleTabRename,
    handleTabCopy,
    handleTabDelete,
    handleTabReorder,
    handleTabPaste,
  };
}
